import Skeleton from '@mui/material/Skeleton'
import React from 'react'

import { SchemaGroup } from '@/__generated/es-api'
import { Form } from '@/app/component/form'
import { ActionsContainer, FormContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import Loading from '@/app/component/guard/loading'
import DeleteProtectionToggle from '@/app/module/contacts/component/form/delete-protection-toggle'
import { operands } from '@/app/module/groups/definitions'
import { useUpdateGroup } from '@/app/module/groups/hooks/use-update-group'
import { PersonalizationType } from '@/app/types'

import { useGetGroupLazy } from '../../hooks/use-get-group-lazy'
import { fields, segmentSchema } from './specs'
import { toConditions, toConditionsFormValue } from './utils'

type Props = {
  personalizationList: PersonalizationType
  segmentId: number

  onExit: () => void
  onSaved?: () => void
}

const UpdateSegmentForm: React.FC<Props> = ({ personalizationList, segmentId, onExit, onSaved }) => {
  const [updateGroup, { isLoading: isUpdating }] = useUpdateGroup()

  const [getGroup, { isFetching }] = useGetGroupLazy({ unsubscribe: true })
  const [data, setData] = React.useState<SchemaGroup | undefined>(undefined)

  React.useEffect(() => {
    const fetchGroup = async (id: number) => {
      const res = await getGroup(id)

      setData(res)
    }

    fetchGroup(segmentId)
  }, [segmentId, getGroup])

  const [noAutodelete, setNoAutodelete] = React.useState(data?.noAutodelete || false)
  const [changed, setChanged] = React.useState(false)
  React.useEffect(() => {
    setNoAutodelete(data?.noAutodelete || false)
  }, [data?.noAutodelete])
  const handleNoAutodeleteChange = React.useCallback((value: boolean) => {
    setNoAutodelete(value)
    setChanged(true)
  }, [])

  const handleSubmit = React.useCallback(
    async (values: { name: string; conditions: string }) => {
      const success = await updateGroup(segmentId, {
        conditions: toConditions(values.conditions),
        type: 'SMART',
        name: values.name,
        description: '',
        noAutodelete,
      })
      if (!success) {
        return
      }
      onSaved?.()
      onExit()
    },
    [noAutodelete, segmentId, onExit, onSaved, updateGroup],
  )

  const segmentFields = React.useMemo(
    () =>
      ({
        ...fields,
        conditions: {
          fieldProps: { label: 'Clauses', name: 'conditions', operands, personalizationList },
          type: 'conditions',
        },
      }) as const,
    [personalizationList],
  )

  const defaultValues = React.useMemo(() => {
    if (!data) {
      return {
        name: '',
        conditions: '[]',
      }
    }

    return {
      name: data?.name || '',
      conditions: toConditionsFormValue(data.conditions),
    }
  }, [data])

  const showSkeleton = !data || isFetching

  return (
    <Loading isLoading={isFetching} testId="segment-item">
      <FormContainer>
        {showSkeleton && <SegmentFormSkeleton />}
        {!showSkeleton && (
          <Form
            defaultValues={defaultValues}
            fields={segmentFields}
            name="update-segment"
            schema={segmentSchema}
            shouldReset
            onSubmit={handleSubmit}
            renderActions={({ isDirty }) => (
              <ActionsContainer>
                <SubmitButton isDirty={changed || isDirty} isSubmitting={isUpdating}>
                  Save
                </SubmitButton>
              </ActionsContainer>
            )}
          >
            <DeleteProtectionToggle checked={noAutodelete} isGroup onChange={handleNoAutodeleteChange} />
          </Form>
        )}
      </FormContainer>
    </Loading>
  )
}

export function SegmentFormSkeleton() {
  return (
    <>
      <Skeleton variant="rectangular" width="100%" height={38} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={38} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" width={100} height={38} sx={{ mb: 2, ml: 'auto', borderRadius: 1 }} />
    </>
  )
}

export default UpdateSegmentForm
